import { useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import type { SurveyQuestion } from '../types/survey';
import { AnswerCard, type AnswerStatus } from './AnswerCard';
import { FeedbackPanel } from './FeedbackPanel';

interface QuestionViewProps {
  question: SurveyQuestion;
  index: number;
  total: number;
  selectedAnswerId: number | null;
  autoAdvance: boolean;
  autoAdvanceCountdown: number;
  autoAdvanceDuration: number;
  onSelect: (position: number) => void;
  onNext: () => void;
}

export function QuestionView({
  question,
  index,
  total,
  selectedAnswerId,
  autoAdvance,
  autoAdvanceCountdown,
  autoAdvanceDuration,
  onSelect,
  onNext,
}: QuestionViewProps) {
  const rootRef = useRef<HTMLElement>(null);
  const correctSoundRef = useRef<HTMLAudioElement | null>(null);
  const wrongSoundRef = useRef<HTMLAudioElement | null>(null);
  const progress = ((index + 1) / total) * 100;
  const isAnswered = selectedAnswerId !== null;
  const isCorrect = selectedAnswerId === question.correctAnswer;

  useEffect(() => {
    const correctSound = new Audio('/assets/sounds/correct.mp3');
    const wrongSound = new Audio('/assets/sounds/wrong.mp3');

    correctSound.preload = 'auto';
    wrongSound.preload = 'auto';
    correctSoundRef.current = correctSound;
    wrongSoundRef.current = wrongSound;

    return () => {
      correctSound.pause();
      wrongSound.pause();
      correctSoundRef.current = null;
      wrongSoundRef.current = null;
    };
  }, []);

  const handleSelect = useCallback((position: number) => {
    if (selectedAnswerId !== null) return;

    const answer = question.answers[position - 1];
    if (!answer) return;

    const sound = answer.id === question.correctAnswer
      ? correctSoundRef.current
      : wrongSoundRef.current;

    if (sound) {
      sound.currentTime = 0;
      void sound.play().catch(() => {
        // Some browsers may still block audio despite the user interaction.
      });
    }

    onSelect(position);
  }, [onSelect, question.answers, question.correctAnswer, selectedAnswerId]);

  useGSAP(
    () => {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) {
        gsap.from(rootRef.current, { opacity: 0, duration: 0.18 });
        return;
      }

      const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
      timeline
        .from('.progress-head', { opacity: 0, y: -12, duration: 0.35 })
        .from('.question-card', { opacity: 0, y: 24, scale: 0.985, duration: 0.52 }, 0.04)
        .from('.answer-card', {
          opacity: 0,
          y: 30,
          scale: 0.97,
          duration: 0.5,
          stagger: 0.09,
        }, 0.14)
        .from('.side-panel', { opacity: 0, x: 28, duration: 0.55 }, 0.16)
        .fromTo('.progress-fill', { width: 0 }, { width: `${progress}%`, duration: 0.7 }, 0.1);
    },
    { scope: rootRef, dependencies: [question.id] },
  );

  const statuses = useMemo(() => {
    return question.answers.map<AnswerStatus>((answer) => {
      if (!isAnswered) return 'idle';
      if (answer.id === question.correctAnswer) return 'correct';
      if (answer.id === selectedAnswerId) return 'wrong';
      return 'dimmed';
    });
  }, [isAnswered, question.answers, question.correctAnswer, selectedAnswerId]);

  return (
    <section ref={rootRef} className="view quiz-view">
      <div className="quiz-main">
        <div className="progress-head">
          <div className="progress-label">Question {index + 1} / {total}</div>
          <div className="progress-track" aria-label="Progression">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <article className="question-card">
          <h1 className="question-card__fr">{question.question.fr}</h1>
          <p className="question-card__ar" dir="rtl">{question.question.ar}</p>
        </article>

        <div className="answers" role="group" aria-label="Choisissez une réponse">
          {question.answers.map((answer, answerIndex) => (
            <AnswerCard
              key={answer.id}
              answer={answer}
              position={answerIndex + 1}
              status={statuses[answerIndex] ?? 'idle'}
              disabled={isAnswered}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>

      <aside className={`side-panel ${isAnswered ? 'has-feedback' : 'is-awaiting-answer'}`}>
        <div className="side-panel__content">
          {!isAnswered ? (
            <div className="side-panel__prompt" aria-live="polite">
              <h2 className="side-panel__prompt-title">À VOUS DE JOUER !</h2>
            </div>
          ) : (
            <div className="side-panel__feedback-state" aria-live="polite">
              <span className="side-panel__label">AIGLE · LE BON GESTE</span>
              <h2 className="side-panel__title">
                {isCorrect ? 'BRAVO !' : 'LE BON RÉFLEXE'}
              </h2>

              <FeedbackPanel question={question} correct={isCorrect} />

              {autoAdvance ? (
                <div
                  className="auto-advance-loader"
                  role="timer"
                  aria-label={`Question suivante dans ${autoAdvanceCountdown} seconde${autoAdvanceCountdown > 1 ? 's' : ''}`}
                >
                  <div
                    className="auto-advance-loader__ring"
                    style={{
                      '--countdown-progress': `${(autoAdvanceCountdown / autoAdvanceDuration) * 360}deg`,
                    } as CSSProperties}
                  >
                    <span className="auto-advance-loader__seconds">{autoAdvanceCountdown}</span>
                  </div>
                  <span className="auto-advance-loader__label">Question suivante</span>
                </div>
              ) : (
                <button type="button" className="next-button is-visible" onClick={onNext}>
                  Question suivante →
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
    </section>
  );
}
